import { Plus, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { chanceAt } from "@/lib/results";
import { fmtMonth, GRID, idx, N, NOW, sortMonths } from "@/lib/months";
import { pct } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useTerm } from "@/model/store";

type By = Record<string, number>;

const clamp = (p: number) => Math.min(0.99, Math.max(0, p));
const r3 = (p: number) => Math.round(p * 1000) / 1000;
const show = (p: number) => String(Math.round(p * 1000) / 10);
const parse = (s: string) => parseFloat(s.replace("%", "").trim()) / 100;

/** Set the chance by `month`, keeping the curve rising: earlier points ≤ v, later points ≥ v. */
function setBy(by: By, month: string, v: number): By {
	const out: By = {};
	for (const [m, p] of Object.entries(by)) out[m] = idx(m) < idx(month) ? Math.min(p, v) : Math.max(p, v);
	out[month] = v;
	return out;
}

/**
 * The baseline as an editable table (PRD §10.3): one row per key date or stated point.
 * "Chance by" edits move that point; "chance in this period" edits shift every later point
 * by the same amount, so later periods keep their chance.
 */
export function EventBaselineTable({
	by,
	statedF,
	onCommit,
	keyDates,
	implied,
	scenario,
	scenarioLabel,
	market,
	marketLabel,
	readOnly,
	valuesLabel = "Baseline",
}: {
	by: By;
	/** The curve through `by` (the API's series): values between points are read off it. */
	statedF: ArrayLike<number>;
	onCommit: (by: By) => void;
	keyDates: string[];
	implied: ArrayLike<number> | null;
	scenario?: ArrayLike<number> | null;
	scenarioLabel?: string;
	market?: (number | null)[] | null;
	marketLabel?: string;
	readOnly?: boolean;
	valuesLabel?: string;
}) {
	const term = useTerm();
	const [newMonth, setNewMonth] = useState("");
	const F = statedF;
	const rows = sortMonths([...new Set([...keyDates, ...Object.keys(by)])]).filter((m) => idx(m) >= 0 && idx(m) < N);
	const main = readOnly ? implied : F;

	function commit(next: By) {
		const out: By = {};
		for (const [m, p] of Object.entries(next)) out[m] = r3(clamp(p));
		onCommit(out);
	}
	function editBy(m: string, v: number) {
		commit(setBy(by, m, clamp(v)));
	}
	function editIn(m: string, prev: string | null, v: number) {
		const before = prev ? chanceAt(F, prev) : 0;
		const target = clamp(before + Math.max(0, v));
		const delta = target - chanceAt(F, m);
		const next: By = { ...by, [m]: target };
		for (const [pm, p] of Object.entries(by)) if (idx(pm) > idx(m)) next[pm] = clamp(p + delta);
		commit(next);
	}

	const cellInput = (value: number, onValue: (v: number) => void, label: string, strong: boolean) => (
		<Input
			key={`${label}:${value}`}
			className={cn("ml-auto h-8 w-24 text-right tabular-nums", !strong && "text-muted-foreground")}
			defaultValue={show(value)}
			inputMode="decimal"
			aria-label={label}
			onBlur={(e) => {
				const v = parse(e.target.value);
				if (Number.isFinite(v) && Math.abs(v - value) > 0.0004) onValue(v);
				else e.target.value = show(value);
			}}
			onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
		/>
	);

	return (
		<div>
			<table className="w-full text-sm tabular-nums">
				<thead>
					<tr className="border-b text-left text-xs text-muted-foreground">
						<th className="py-2 pr-4 font-normal">Date</th>
						<th className="px-2 py-2 text-right font-normal">{readOnly ? "Calculated: chance by" : term(`${valuesLabel}: chance by (%)`, "stated CDF")}</th>
						<th className="px-2 py-2 text-right font-normal">
							{readOnly ? "Chance in this period" : term("Chance in this period (%)", "mass since previous row")}
						</th>
						{!readOnly && <th className="px-2 py-2 text-right font-normal text-[var(--model)]">{term("Model", "implied")}</th>}
						{scenario && <th className="px-2 py-2 text-right font-normal text-[var(--scenario-ink)]">{scenarioLabel}</th>}
						{market && <th className="px-2 py-2 text-right font-normal text-[var(--market)]">{marketLabel}</th>}
						<th className="w-8" />
					</tr>
				</thead>
				<tbody>
					{rows.map((m, i) => {
						const prev = i > 0 ? rows[i - 1] : null;
						const stated = m in by;
						const cBy = main ? chanceAt(main, m) : 0;
						const cIn = main ? cBy - (prev ? chanceAt(main, prev) : 0) : 0;
						const mk = market ? market[Math.min(N - 1, idx(m))] : null;
						return (
							<tr key={m} className="border-b last:border-0">
								<td className="py-1.5 pr-4">
									<div className={cn(stated && !readOnly && "font-medium")}>By {fmtMonth(m)}</div>
									<div className="text-[11px] text-muted-foreground">
										period since {fmtMonth(prev ?? NOW)}
										{!stated && !readOnly && " · filled in"}
									</div>
								</td>
								<td className="px-2 py-1.5 text-right">{readOnly ? pct(cBy) : cellInput(cBy, (v) => editBy(m, v), `Chance by ${fmtMonth(m)}`, stated)}</td>
								<td className="px-2 py-1.5 text-right">
									{readOnly ? pct(cIn) : cellInput(cIn, (v) => editIn(m, prev, v), `Chance between ${fmtMonth(prev ?? NOW)} and ${fmtMonth(m)}`, stated)}
								</td>
								{!readOnly && <td className="px-2 py-1.5 text-right text-[var(--model)]">{implied ? pct(chanceAt(implied, m)) : "…"}</td>}
								{scenario && <td className="px-2 py-1.5 text-right text-[var(--scenario-ink)]">{pct(chanceAt(scenario, m))}</td>}
								{market && (
									<td className={cn("px-2 py-1.5 text-right", mk === null || mk === undefined ? "text-xs text-muted-foreground/60" : "text-[var(--market)]")}>
										{mk === null || mk === undefined ? "no view" : pct(mk)}
									</td>
								)}
								<td className="py-1.5 pl-1 text-right">
									{!readOnly && stated && Object.keys(by).length > 1 && (
										<Button
											variant="ghost"
											size="icon"
											className="size-7"
											title="Remove this point"
											aria-label={`Remove the point at ${fmtMonth(m)}`}
											onClick={() => {
												const next = { ...by };
												delete next[m];
												commit(next);
											}}
										>
											<X className="size-3.5" />
										</Button>
									)}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			{!readOnly && (
				<div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
					<Input
						type="month"
						className="h-8 w-44"
						min={GRID[0]}
						max={GRID[GRID.length - 1]}
						value={newMonth}
						onChange={(e) => setNewMonth(e.target.value)}
						aria-label="New date"
					/>
					<Button
						size="sm"
						variant="outline"
						disabled={!newMonth || idx(newMonth) < 0 || idx(newMonth) >= N || newMonth in by}
						onClick={() => {
							commit({ ...by, [newMonth]: chanceAt(F, newMonth) });
							setNewMonth("");
						}}
					>
						<Plus className="size-3.5" /> Add date
					</Button>
					<span className="text-xs text-muted-foreground">
						Rows marked “filled in” are between entered points; typing a value turns them into points. Changing a period’s chance shifts every later date by the
						same amount.
					</span>
				</div>
			)}
		</div>
	);
}
